import { ChevronLeft } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import { Segmented } from "./Controls";
import CountyMap from "./CountyMap";
import { CountyLegend, NationalLegend } from "./Legend";
import type { County2024 } from "./model";
import NationalMap from "./NationalMap";
import type { Layer, ModelSettings, Office, Paint } from "./types";
import {
  type CountyLayer,
  countyPaints,
  OFFICE_LABEL,
  type RaceRow,
} from "./view";

/* ── Types ─────────────────────────────────────── */

interface TrackerMapProps {
  county2024: County2024 | null;
  /** The `turnoutEdge` the row was built with. */
  edge: number;
  /** The `nationalEnvironment` the row was built with. */
  environment: number | null;
  layer: Layer;
  office: Office;
  /** Back to the national map. */
  onBack: () => void;
  /** A state shape was clicked, by FIPS. */
  onSelect: (fips: string) => void;
  /** National map fills, by state FIPS. */
  paints: Record<string, Paint>;
  /** The open state's race; null on the national map. */
  row: RaceRow | null;
  settings: ModelSettings;
  showIndependent: boolean;
  sourceLabel: string | null;
}

/* ── Constants ─────────────────────────────────── */

const COUNTY_LAYERS: { label: string; value: CountyLayer }[] = [
  { label: "Foresight", value: "foresight" },
  { label: "2024 result", value: "2024" },
  { label: "Shift vs 2024", value: "shift" },
];

/* ── Component ─────────────────────────────────── */

/** The national map, or the open state's county map, with its legend. */
const TrackerMap: FC<TrackerMapProps> = ({
  county2024,
  edge,
  environment,
  layer,
  office,
  onBack,
  onSelect,
  paints,
  row,
  settings,
  showIndependent,
  sourceLabel,
}) => {
  const [countyLayer, setCountyLayer] = useState<CountyLayer>("foresight");
  const [features, setFeatures] = useState<Record<string, GeoJSON.Feature>>({});

  const countyMapPaints = useMemo(
    () =>
      row
        ? countyPaints({
            county2024,
            edge,
            environment,
            layer: countyLayer,
            office,
            row,
            settings,
          })
        : {},
    [county2024, countyLayer, edge, environment, office, row, settings]
  );

  return (
    <Card className="flex-1 w-full min-w-0 p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-4">
        {row ? (
          <HStack className="flex-wrap items-center justify-between gap-3">
            <Pressable className="flex-row items-center gap-1" onPress={onBack}>
              <ChevronLeft color="rgb(var(--color-typography-600))" size={16} />
              <Text className="text-sm font-medium text-typography-700">
                United States
              </Text>
              <Text className="text-sm text-typography-400">
                {" "}
                / {row.state.name} counties
              </Text>
            </Pressable>
            <Segmented
              onChange={setCountyLayer}
              options={COUNTY_LAYERS}
              value={countyLayer}
            />
          </HStack>
        ) : (
          <Text className="text-sm text-typography-500">
            {office === "house"
              ? "Generic ballot by state: all 435 House seats are up. Click a state for its counties."
              : `Only states with a ${OFFICE_LABEL[office]} race on the 2026 ballot are colored. Click one for its counties.`}
          </Text>
        )}

        <Box className="w-full max-w-[860px] self-center">
          {row && (
            <CountyMap
              key={row.state.fips}
              paints={countyMapPaints}
              stateFeature={features[row.state.fips] ?? null}
              stateFips={row.state.fips}
            />
          )}
          {/* Stays mounted under the county view: it loads the state
              shapes the county map zooms on, even when a state is
              picked from the list before the map has drawn. */}
          <Box className={row ? "hidden" : "w-full"}>
            <NationalMap
              onFeatures={setFeatures}
              onSelect={onSelect}
              paints={paints}
              selected={row?.state.fips ?? null}
            />
          </Box>
        </Box>

        {row ? (
          <CountyLegend layer={countyLayer} />
        ) : (
          <NationalLegend
            layer={layer}
            showIndependent={showIndependent}
            sourceLabel={sourceLabel}
          />
        )}
      </VStack>
    </Card>
  );
};

export default TrackerMap;
